import { create } from "zustand"
import {
  createJSONStorage,
  persist,
  type StateStorage,
} from "zustand/middleware"

import type { GeocodeV4Response } from "@be-miniapp/api"
import type { Location } from "@be-miniapp/types"

import type { RideLocation } from "./ride-data"

interface UserPersistingData {
  lastSelectedVehicleType?: number
  setLastSelectedVehicleType: (vehicleType: number) => void
  _hasHydrated: boolean
  setHasHydrated: (value: boolean) => void
  lastLocationTimestamp?: number
  isLocationManuallyChanged?: boolean
  location: Location | null
  setLocation: (location: Location, manual?: boolean) => void
  locationDetail: RideLocation | null
  setLocationDetail: (location: RideLocation) => void
  locationAddress: GeocodeV4Response | null
  setLocationAddress: (locationAddress: GeocodeV4Response | null) => void
  clearLocationCache: () => void
  locationPermissionState: {
    status: "accepted" | "denied" | "not-asked" | "unavailable"
    errorCode?: number
  }
  setLocationPermissionState: (
    status: "accepted" | "denied" | "not-asked" | "unavailable",
    code?: number
  ) => void
}

const useUserPersistingData = create<
  UserPersistingData,
  [["zustand/persist", UserPersistingData]]
>(
  persist(
    (set) => ({
      location: null,
      lastLocationTimestamp: 0,
      locationDetail: null,
      locationAddress: null,
      setLocationDetail: (location) => {
        set({ locationDetail: location })
      },
      setLocationAddress: (locationAddress) => {
        set({ locationAddress })
      },
      setLocation: (location: Location, manual?: boolean) => {
        set({
          location,
          lastLocationTimestamp: Date.now(),
          isLocationManuallyChanged: !!manual,
        })
      },
      clearLocationCache: () => {
        set({
          location: null,
          locationDetail: null,
          locationAddress: null,
          lastLocationTimestamp: 0,
          isLocationManuallyChanged: false,
        })
      },
      locationPermissionState: {
        status: "not-asked",
        errorCode: 0,
      },

      setLastSelectedVehicleType(vehicleType: number) {
        set({ lastSelectedVehicleType: vehicleType })
      },
      setLocationPermissionState(
        status: "accepted" | "denied" | "not-asked" | "unavailable",
        code: number = 0
      ) {
        set({
          locationPermissionState: {
            status,
            errorCode: code,
          },
        })
      },

      _hasHydrated: false,
      setHasHydrated: (state: boolean) => {
        set({
          _hasHydrated: state,
        })
      },
    }),
    {
      name: "be-user-permission-store",
      storage: createJSONStorage(() => localStorage as unknown as StateStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true)
      },
    }
  )
)

export { useUserPersistingData }
